import React, { useEffect, useState, useCallback, useRef } from "react";
import adminTemplatesService, {
  AdminTemplate,
  DoctorOption,
  DoctorTemplateAssignment,
} from "../../../services/adminTemplatesService";

interface TemplateAssignModalProps {
  template: AdminTemplate;
  onClose: () => void;
}

const TemplateAssignModal: React.FC<TemplateAssignModalProps> = ({
  template,
  onClose,
}) => {
  const [assignments, setAssignments] = useState<DoctorTemplateAssignment[]>(
    [],
  );
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assigned, allDoctors] = await Promise.all([
        adminTemplatesService.assignments(template.id),
        adminTemplatesService.listDoctors(),
      ]);
      setAssignments(assigned);
      setDoctors(allDoctors);
    } catch (err: any) {
      setError(err.message || "Failed to load assignments");
    } finally {
      setLoading(false);
    }
  }, [template.id]);

  useEffect(() => {
    load();
  }, [load]);

  const assignedDoctorIds = new Set(assignments.map((a) => a.doctor));
  const availableDoctors = doctors.filter(
    (d) => !assignedDoctorIds.has(d.doctor_id),
  );

  const handleAssign = async () => {
    if (!selectedDoctorId) return;
    setBusy(true);
    setError(null);
    try {
      await adminTemplatesService.assign(template.id, Number(selectedDoctorId));
      setSelectedDoctorId("");
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const handleUnassign = async (doctorId: number) => {
    setBusy(true);
    setError(null);
    try {
      await adminTemplatesService.unassign(template.id, doctorId);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Unassign failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Assign Doctors</h2>
            <p className="text-xs text-gray-500 mt-0.5">{template.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="flex gap-2">
            <div ref={dropdownRef} className="relative flex-1">
              <button
                type="button"
                onClick={() =>
                  !busy &&
                  availableDoctors.length > 0 &&
                  setDropdownOpen((o) => !o)
                }
                disabled={busy || availableDoctors.length === 0}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-left
                  focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50
                  bg-white flex items-center justify-between"
              >
                <span
                  className={
                    selectedDoctorId ? "text-gray-900" : "text-gray-400"
                  }
                >
                  {selectedDoctorId
                    ? (() => {
                        const d = availableDoctors.find(
                          (x) => String(x.doctor_id) === selectedDoctorId,
                        );
                        return d
                          ? `${d.name}${d.specialty_display ? ` (${d.specialty_display})` : ""}`
                          : "Select a doctor...";
                      })()
                    : availableDoctors.length === 0
                      ? "All doctors already assigned"
                      : "Select a doctor to assign..."}
                </span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {dropdownOpen && (
                <ul
                  className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg
                  shadow-lg max-h-48 overflow-y-auto"
                >
                  {availableDoctors.map((d) => (
                    <li
                      key={d.doctor_id}
                      onClick={() => {
                        setSelectedDoctorId(String(d.doctor_id));
                        setDropdownOpen(false);
                      }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700
                        ${String(d.doctor_id) === selectedDoctorId ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-900"}`}
                    >
                      {d.name}
                      {d.specialty_display ? ` (${d.specialty_display})` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={handleAssign}
              disabled={busy || !selectedDoctorId}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700
                rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Assign
            </button>
          </div>

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Loading...
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Not assigned to any doctors yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
              {assignments.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {a.doctor_name}
                    </div>
                    {a.is_default && (
                      <span className="text-xs font-semibold text-blue-600">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleUnassign(a.doctor)}
                      disabled={busy}
                      className="text-xs font-semibold text-red-500 hover:text-red-700 transition disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateAssignModal;
